// /billing: my plan (RFC-065, byj-1wv.132.8) — the plans on sale with what
// each grants, bought in the payment provider's window; mine once bought,
// its period, whether a card renews it, cancelled or taken back; and my
// orders with their receipts. The window comes back here with what the
// relay confirms (a payment) or charges (a card), said once and then left
// off the address. /pricing is the same page's plans, signed in or not.
// Only what the relay says is shown: the prices are its plans', the plan
// mine is its subscription.
import { useCallback, useEffect, useRef, useState } from "react";
import { api, type BillingOrder, type BillingView, type Checkout, type ClassSummary, type Plan, type PlansView } from "../api";
import { allowance, openWindow, price, returned } from "../billing";
import { endOfDay } from "../components/Admins";
import { useConfirm } from "../components/Confirm";
import { FormDialog } from "../components/FormDialog";
import { Page, PageHead, PageLead } from "../components/Page";
import { EmptyRows, Grouped, initialOf } from "../components/Rows";
import { StateBar } from "../components/StateBar";
import { navigate } from "../router";
import { dayInput, t, whenDay } from "../i18n";

const errText = (err: unknown) => (err instanceof Error ? err.message : String(err));

/** A plan's price and its period: ₩19,900 a month. */
const priced = (p: Pick<Plan, "price" | "currency" | "period">) =>
  p.period === "once"
    ? price(p.price, p.currency)
    : t(p.period === "term" ? "billing:perSeat" : p.period === "year" ? "billing:perYear" : "billing:perMonth", { price: price(p.price, p.currency) });

/** What a plan grants, in words. */
function grantsOf(p: Plan): string {
  const a = allowance(p);
  if (p.family === "class") return t("billing:grantSeat", { hours: a.creditHours ?? 0, gb: a.gb ?? 0 });
  return [a.hours && t("billing:grantHours", { n: a.hours }), a.gb && t("billing:grantFiles", { n: a.gb }), a.creditHours && t("billing:grantCredits", { n: a.creditHours })]
    .filter(Boolean)
    .join(" · ");
}

function PlanRow({ p, can, busy, onBuy, onSeats }: { p: Plan; can: boolean; busy: boolean; onBuy: (p: Plan, pay: "card" | "once") => void; onSeats: (p: Plan) => void }) {
  return (
    <li className="share-row no-menu" data-testid="plan">
      <span className="row-lead" />
      <span className="kind-box" aria-hidden="true">
        {initialOf(p.name)}
      </span>
      <div className="row-main">
        <span className="row-title">{p.name}</span>
        <div className="row-sub">{[priced(p), grantsOf(p)].filter(Boolean).join(" · ")}</div>
      </div>
      <div className="row-meta">
        {can && p.recurring && (
          <button className="primary small" disabled={busy} onClick={() => onBuy(p, "card")} data-testid="buy-card">
            {t("billing:buyCard")}
          </button>
        )}
        {can && p.family === "class" && (
          <button className="small" disabled={busy} onClick={() => onSeats(p)} data-testid="buy-seats" aria-controls="seats-dialog">
            {t("billing:buySeats")}
          </button>
        )}
        {can && p.family !== "class" && (
          <button className="small" disabled={busy} onClick={() => onBuy(p, "once")} data-testid="buy-once">
            {p.recurring ? t("billing:buyOnce") : t("billing:buy")}
          </button>
        )}
      </div>
    </li>
  );
}

/** The dialog that buys a class's seats: one of the classes I teach, ten seats or more, the term's last day. */
function SeatsDialog({ plan, classes, onBuy, onClose }: {
  plan: Plan;
  classes: typeof api.classes;
  onBuy: (seats: { class_id: string; seats: number; ends_at: string }) => Promise<void>;
  onClose: () => void;
}) {
  const [mine, setMine] = useState<ClassSummary[] | null>(null);
  const [cls, setCls] = useState("");
  const [seats, setSeats] = useState("10");
  const [until, setUntil] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() + 4);
    return dayInput(d);
  });
  useEffect(() => {
    void classes().then((cs) => {
      const taught = cs.filter((c) => c.role === "instructor");
      setMine(taught);
      if (taught[0]) {
        setCls(taught[0].id);
        setSeats(String(Math.max(10, taught[0].members - 1)));
      }
    });
  }, [classes]);
  const n = Number(seats);
  const ends = endOfDay(until);
  return (
    <FormDialog
      id="seats-dialog"
      testid="seats-form"
      title={t("billing:seatsTitle")}
      now={Number.isInteger(n) && n >= 10 ? t("billing:seatsTotal", { price: price(plan.price * n, plan.currency) }) : undefined}
      submitLabel={t("billing:buySeats")}
      canSubmit={!!cls && Number.isInteger(n) && n >= 10 && n <= 1000 && !!ends}
      onSubmit={() => onBuy({ class_id: cls, seats: n, ends_at: ends! })}
      onClose={onClose}
    >
      {mine && mine.length === 0 && <p className="muted small flush">{t("billing:seatsNoClass")}</p>}
      <label className="field">
        {t("billing:seatsClass")}
        <select value={cls} onChange={(e) => setCls(e.target.value)} aria-label={t("billing:seatsClass")}>
          {(mine ?? []).map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </label>
      <label className="field">
        {t("billing:seatsCount")}
        <input type="number" min={10} max={1000} value={seats} onChange={(e) => setSeats(e.target.value)} aria-label={t("billing:seatsCount")} />
      </label>
      <label className="field">
        {t("billing:seatsUntil")}
        <input type="date" value={until} onChange={(e) => setUntil(e.target.value)} aria-label={t("billing:seatsUntil")} />
      </label>
    </FormDialog>
  );
}

function OrderRow({ o }: { o: BillingOrder }) {
  const sub = [price(o.amount, o.currency), o.method, whenDay(o.paid_at ?? o.created_at)].filter(Boolean).join(" · ");
  return (
    <li className={`share-row no-menu ${o.status === "paid" ? "is-live" : "is-ended"}`} data-testid="order">
      <span className="row-lead" />
      <span className="kind-box" aria-hidden="true">
        {initialOf(o.name)}
      </span>
      <div className="row-main">
        <span className="row-title">{o.name}</span>
        <div className="row-sub">{sub}</div>
      </div>
      <div className="row-meta">
        <span className={`chip${o.status === "failed" ? " chip-warn" : ""}`}>{t(`billing:order_${o.status}`)}</span>
        {o.receipt_url && (
          <a className="small" href={o.receipt_url} target="_blank" rel="noreferrer">
            {t("billing:receipt")}
          </a>
        )}
      </div>
    </li>
  );
}

export function Billing({
  signedIn,
  query,
  plans = api.plans,
  billing = api.billing,
  checkout = api.checkout,
  confirmPayment = api.confirmPayment,
  registerCard = api.registerCard,
  cancelPlan = api.cancelPlan,
  resumePlan = api.resumePlan,
  classes = api.classes,
  open = (c: Checkout) => openWindow(c, window.location.origin, (to) => navigate(to)),
}: {
  signedIn: boolean;
  query: URLSearchParams;
  plans?: typeof api.plans;
  billing?: typeof api.billing;
  checkout?: typeof api.checkout;
  confirmPayment?: typeof api.confirmPayment;
  registerCard?: typeof api.registerCard;
  cancelPlan?: typeof api.cancelPlan;
  resumePlan?: typeof api.resumePlan;
  classes?: typeof api.classes;
  open?: (c: Checkout) => Promise<void>;
}) {
  const [onSale, setOnSale] = useState<PlansView | null>(null);
  const [mine, setMine] = useState<BillingView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [seatsFor, setSeatsFor] = useState<Plan | null>(null);
  const [ask, confirmDialog] = useConfirm();

  const load = useCallback(async () => {
    try {
      // both at once, so the plans are never drawn before mine says which are on offer
      const [p, m] = await Promise.all([plans(), signedIn ? billing() : Promise.resolve(null)]);
      if (m) setMine(m);
      setOnSale(p);
    } catch (err) {
      setError(errText(err));
    }
  }, [plans, billing, signedIn]);

  // what the window brought back, settled once, then left off the address
  const back = returned(query);
  const settling = useRef(false);
  useEffect(() => {
    if (!back || !signedIn || settling.current) {
      void load();
      return;
    }
    settling.current = true;
    void (async () => {
      try {
        if (back.step === "fail") setError(back.message || t("billing:windowFailed"));
        else {
          const s = back.step === "paid" ? await confirmPayment(back.order, back.paymentKey, back.amount) : await registerCard(back.order, back.authKey, back.customerKey);
          setDone(s.waiting === "deposit" ? t("billing:waitingDeposit") : t("billing:paid", { name: s.order.name }));
          setError(null);
        }
      } catch (err) {
        setError(errText(err));
      }
      navigate("/billing", true);
      await load();
    })();
    // the query is read once, as the page opens
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buy = async (p: Plan, pay: "card" | "once") => {
    setBusy(true);
    setError(null);
    try {
      // the window leaves the page; the fake's comes back to it at once
      await open(await checkout(p.id, pay));
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  };

  const buySeats = async (p: Plan, seats: { class_id: string; seats: number; ends_at: string }) => {
    setError(null);
    // a refusal is said in the dialog, which stays open
    const c = await checkout(p.id, "once", seats);
    setSeatsFor(null);
    await open(c);
  };

  const cancel = async () => {
    if (!(await ask(t("billing:cancelAsk"), { action: t("billing:cancel"), danger: true }))) return;
    try {
      const r = await cancelPlan();
      setDone(r.refunded ? t("billing:refunded", { amount: price(r.amount ?? 0, r.currency ?? "KRW") }) : t("billing:cancelled"));
      setError(null);
    } catch (err) {
      setError(errText(err));
    }
    await load();
  };

  const resume = async () => {
    try {
      await resumePlan();
      setDone(t("billing:resumed"));
      setError(null);
    } catch (err) {
      setError(errText(err));
    }
    await load();
  };

  const sub = mine?.subscription ?? null;
  const configured = !!onSale?.configured;
  return (
    <Page className="billing-page">
      <PageHead title={signedIn ? t("billing:title") : t("billing:pricingTitle")} meta={configured && !onSale?.live && <span className="chip">{t("billing:testMode")}</span>} />
      <PageLead>{t("billing:lead")}</PageLead>
      {confirmDialog}
      {error && <p className="error">{t("common:error", { m: error })}</p>}
      {done && (
        <p className="ok" data-testid="billing-done">
          {done}
        </p>
      )}
      {onSale && !configured && (
        <section className="card pad" data-testid="billing-off">
          <p className="flush">{t("billing:off")}</p>
        </section>
      )}
      {sub && (
        <StateBar
          testid="my-plan"
          label={t("billing:myPlan")}
          state={sub.status === "active" ? "live" : "warn"}
          stateLabel={<strong>{sub.plan.name}</strong>}
          actions={
            sub.cancel_at_end ? (
              sub.card && (
                <button className="small" onClick={() => void resume()}>
                  {t("billing:resume")}
                </button>
              )
            ) : (
              <button className="small danger" onClick={() => void cancel()} data-testid="cancel-plan">
                {t("billing:cancel")}
              </button>
            )
          }
        >
          <span className="muted small">
            {[
              sub.status === "past_due" ? t("billing:pastDue") : null,
              sub.renews ? t("billing:renewsOn", { w: whenDay(sub.period_end), card: sub.card ?? "" }) : t("billing:endsOn", { w: whenDay(sub.period_end) }),
              sub.next && sub.renews ? t("billing:nextPrice", { price: price(sub.next.price, sub.next.currency), w: whenDay(sub.next.from) }) : null,
            ]
              .filter(Boolean)
              .join(" · ")}
          </span>
        </StateBar>
      )}
      {!onSale && !error && <p className="muted">{t("common:loading")}</p>}
      {mine?.seats && mine.seats.length > 0 && (
        <p className="small" data-testid="seats-held">
          {mine.seats.map((x) => t("billing:seatsHeld", { name: x.class_name, taken: x.taken, seats: x.seats, w: whenDay(x.ends_at) })).join(" · ")}
        </p>
      )}
      {mine?.credits && mine.credits.length > 0 && (
        <p className="small" data-testid="credits-left">
          {mine.credits.map((c) => t("billing:creditsLeft", { n: Math.floor(c.seconds_left / 3600), w: whenDay(c.expires_at) })).join(" · ")}
        </p>
      )}
      {onSale && (
        <div className="rows-panel">
          <Grouped
            id="plans"
            headed
            // with a plan open, only what is bought beside it: credit packs
            groups={[{ title: t("billing:plans"), items: onSale.plans.filter((p) => !sub || p.family === "credits" || p.family === "class"), none: <EmptyRows text={t("billing:noPlans")} />, testid: "plans" }]}
            row={(p) => <PlanRow key={p.id} p={p} can={signedIn && configured} busy={busy} onBuy={(x, pay) => void buy(x, pay)} onSeats={setSeatsFor} />}
          />
        </div>
      )}
      {onSale && !signedIn && (
        <p className="muted small">
          <a href="/billing" onClick={(e) => (e.preventDefault(), navigate("/billing"))}>
            {t("billing:signInToBuy")}
          </a>
        </p>
      )}
      {mine && (
        <div className="rows-panel">
          <Grouped
            id="orders"
            headed
            groups={[{ title: t("billing:orders"), items: mine.orders, none: <EmptyRows text={t("billing:noOrders")} />, testid: "orders" }]}
            row={(o) => <OrderRow key={o.id} o={o} />}
          />
        </div>
      )}
      {seatsFor && <SeatsDialog plan={seatsFor} classes={classes} onBuy={(x) => buySeats(seatsFor, x)} onClose={() => setSeatsFor(null)} />}
      <p className="muted small">{t("billing:vat")}</p>
    </Page>
  );
}
